import React, { useState, useEffect } from "react";
import {
  Card,
  Avatar,
  Row,
  Col,
  Button,
  Form,
  Input,
  Upload,
  DatePicker,
  message,
  Divider,
  Typography,
  Spin,
  Select,
  Tabs
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  LockOutlined,
  UploadOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { FaEdit } from "react-icons/fa";
import moment from "moment";
import dayjs, { Dayjs } from 'dayjs';

import { getprofile, updateprofile, fetchDesignations } from "../../utils/API";
import TabPane from "antd/es/tabs/TabPane";

interface Designation {
  _id: string;
  name: string;
}

const { Title, Text } = Typography;

interface User {
  _id: string | null;
  photo: any;
  username: string;
  password: string | null;
  designation: string;
  email: string;
  mobileNo: string;
  dob: string;
  designationName?: string;
}

export const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User>({
    _id: "",
    photo: "",
    username: "",
    password: "",
    designation: "",
    email: "",
    mobileNo: "",
    dob: "",
  });


  const fetchData = async () => {
    setLoading(true);
    try {
      const designationResponse = await fetchDesignations("", "");

      let extractedDesignations: Designation[] = [];

      if (Array.isArray(designationResponse)) {
        extractedDesignations = designationResponse;
      } else if (designationResponse?.data && Array.isArray(designationResponse.data)) {
        extractedDesignations = designationResponse.data;
      } else {
        console.error("Invalid designation response:", designationResponse);
      }

      setDesignations(extractedDesignations);

      const profileResponse = await getprofile({});

      const designation: Designation | undefined = extractedDesignations.find(
        (d: Designation) => d._id === profileResponse.designation?._id
      );
      const userData = {
        _id: profileResponse._id || null,
        photo: profileResponse.photo
          ? `${process.env.REACT_APP_IMAGE_URL}/developers/${profileResponse.photo}`
          : process.env.PUBLIC_URL + "/download.png",
        username: profileResponse.username || "",
        password: profileResponse.password || null,
        designation: profileResponse.designation?._id || profileResponse.designation || "",
        designationName: designation?.name || profileResponse.designation?.name || "",
        email: profileResponse.email || "",
        mobileNo: profileResponse.mobileNo || "",
        dob: profileResponse.dob ? dayjs(profileResponse.dob).format("DD-MM-YYYY") : "",
      };

      setUser(userData);

      form.setFieldsValue({
        photo: userData.photo,
        designationName: userData.designationName,
        _id: userData._id,
        username: userData.username,
        designation: userData.designation,
        email: userData.email,
        mobileNo: userData.mobileNo,
        dob: userData.dob ? dayjs(userData.dob, "DD-MM-YYYY") : null,
      });

    } catch (error) {
      console.error("Failed to load data:", error);
      message.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };



  const onFinishProfileEdit = async (values: any) => {
    setIsUpdating(true);
    try {
      const formData = new FormData();

      formData.append("_id", user._id || "");
      if (values.username) {
        formData.append("username", values.username);
      }
      if (values.designation) {
        formData.append("designation", values.designation);
      }
      if (values.email) {
        formData.append("email", values.email);
      }
      if (values.mobileNo) {
        formData.append("mobileNo", values.mobileNo);
      }
      if (values.dob) {
        formData.append("dob", values.dob);
      }

      // Convert base64 image to Blob before appending if necessary
      if (user.photo && user.photo.startsWith("data:image")) {
        const blob = await fetch(user.photo).then(res => res.blob());
        formData.append("photo", blob, "profile.jpg");
      }

      const response = await updateprofile(formData);
      if (response) {
        const authDataString = localStorage.getItem("authData");
        const existingAuthData = authDataString ? JSON.parse(authDataString) : null;

        if (existingAuthData) {
          const updatedAuthData = {
            token: existingAuthData.token,
            user: response.updatedUser,
          };

          localStorage.setItem("authData", JSON.stringify(updatedAuthData));
        } else {
          console.warn("No existing authData found in localStorage.");
        }

        const updatedDesignation = designations.find(d => d._id === response.designation);

        const updatedUser = {
          ...user,
          photo: response.photo || user.photo,
          username: response.username || user.username,
          designation: response.designation || user.designation,
          designationName: updatedDesignation?.name || user.designationName,
          email: response.email || user.email,
          mobileNo: response.mobileNo || user.mobileNo,
          dob: response.dob || user.dob,
        };

        setUser(updatedUser);
        form.setFieldsValue({
          dob: updatedUser.dob ? dayjs(updatedUser.dob, "DD-MM-YYYY") : null,
        });
        message.success("Profile updated successfully!");
        fetchData()
      }
    } catch (error: any) {
      console.error("Update error:", error);
      message.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setIsUpdating(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  const onFinishPasswordChange = async (values: any) => {
    setIsUpdating(true);
    try {
      // Validate that new password and confirm password match
      if (values.newPassword !== values.confirmPassword) {
        throw new Error("New password and confirm password do not match");
      }

      const formData = new FormData();
      formData.append("_id", user._id || "");
      formData.append("currentPassword", values.currentPassword);
      formData.append("newPassword", values.newPassword);

      const response = await updateprofile(formData);

      if (response) {
        message.success("Password updated successfully!");
        passwordForm.resetFields();
      }
    } catch (error: any) {
      console.error("Password update error:", error);
      message.error(error.response?.data?.message || error.message || "Failed to update password");
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const newPhoto = reader.result as string;
      setUser(prev => ({ ...prev, photo: newPhoto }));
      form.setFieldsValue({ photo: newPhoto });
      message.success("Photo updated!");
    };
    reader.readAsDataURL(file);
    return false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-2 from-[#f0f4ff] to-[#e2eafc] min-h-screen">
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} sm={24} md={24}>
          <Card
            className="rounded-2xl border border-gray-100 backdrop-blur-md bg-white/70"
            bodyStyle={{ padding: 0 }}
          >
            <div className="p-6">
              <Tabs
                defaultActiveKey="1"
                tabBarGutter={32}
                className="text-left"
                tabPosition="top"
              >
                {/* Update Profile Tab */}
                <TabPane tab="Update Profile" key="1">
                  <div className="max-w-xl mx-auto border border-gray-300 rounded-lg p-6 shadow-md bg-white">
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={onFinishProfileEdit}
                      validateTrigger="onBlur"
                    >
                      <div className="text-center mb-6">
                        <div className="relative inline-block">
                          <Avatar
                            size={100}
                            src={user.photo || `${process.env.REACT_APP_IMAGE_URL}/download.png`}
                            className=""
                          />
                          <Form.Item name="photo" className="!mb-0 absolute -bottom-1 -right-2">
                            <Upload
                              beforeUpload={handlePhotoUpload}
                              showUploadList={false}
                              accept="image/*"
                            >
                              <Button
                                icon={<UploadOutlined />}
                                shape="circle"
                                size="small"
                                className="shadow-md"
                                style={{
                                  backgroundColor: "#001529",
                                  borderColor: "#001529",
                                  color: "white",
                                }}
                              />
                            </Upload>
                          </Form.Item>
                        </div>
                        <Title level={4} className="text-[#1d3557] mt-4">
                          {user.username || "No name"}
                        </Title>
                      </div>

                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Name <span className="text-danger">*</span></span>}
                            name="username"
                            rules={[{ required: false, message: 'Please input your name!' }]}
                          >
                            <Input prefix={<UserOutlined />} className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Designation <span className="text-danger">*</span></span>}
                            name="designation"
                            rules={[{ required: false, message: 'Please select your designation!' }]}
                          >
                            <Select placeholder="Select designation" allowClear className="CustomSelect">
                              {designations.map(d => (
                                <Select.Option key={d._id} value={d._id}>
                                  {d.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Birthdate <span className="text-danger">*</span></span>}
                            name="dob"
                            rules={[{ required: false, message: 'Please select your birthdate!' }]}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD-MM-YYYY"
                              className="Allinput"
                              value={user.dob ? dayjs(user.dob, "DD-MM-YYYY") : null}
                              disabledDate={current => current && current.isAfter(dayjs().endOf("day"))}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Email <span className="text-danger">*</span></span>}
                            name="email"
                            rules={[
                              { required: false, message: 'Please input your email!' },
                              { type: 'email', message: 'Please enter a valid email!' }
                            ]}
                          >
                            <Input prefix={<MailOutlined />} className="Allinput" />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Phone Number <span className="text-danger">*</span></span>}
                            name="mobileNo"
                            rules={[
                              { required: false, message: 'Please input your phone number!' },
                              { pattern: /^[0-9]{10,15}$/, message: 'Please enter a valid phone number!' }
                            ]}
                          >
                            <Input prefix={<PhoneOutlined />} className="Allinput" />
                          </Form.Item>
                        </Col>


                        <Col
                          xs={24}
                          sm={24}
                          md={24}
                          xl={24}
                          xxl={20}
                          className="d-flex justify-content-end me-3"
                        >

                          <button className="allsubmitebutton "
                            type="submit"
                            disabled={isLoading}>
                            {isLoading ? "Updating Profile..." : "Update Profile"}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </TabPane>

                {/* Update Password Tab */}
                <TabPane tab="Update Password" key="2">
                  <div className="max-w-xl mx-auto border border-gray-300 rounded-lg p-6 shadow-md bg-white">
                    <Form
                      form={passwordForm}
                      layout="vertical"
                      onFinish={onFinishPasswordChange}
                      validateTrigger="onBlur"
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Current Password <span className="text-danger">*</span></span>}
                            name="currentPassword"
                            rules={[
                              { required: false, message: 'Please input your current password!' },
                              // { min: 6, message: 'Password must be at least 6 characters!' }
                            ]}
                          >
                            <Input.Password
                              prefix={<LockOutlined />}
                              placeholder="Enter current password"
                              className="Allinput"
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">New Password <span className="text-danger">*</span></span>}
                            name="newPassword"
                            rules={[
                              { required: false, message: 'Please input your new password!' },
                              // { min: 6, message: 'Password must be at least 6 characters!' }
                            ]}
                          >
                            <Input.Password
                              prefix={<LockOutlined />}
                              className="Allinput"
                              placeholder="Enter new password"
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            label={<span className="Inputlabel">Confirm Password <span className="text-danger">*</span></span>}
                            name="confirmPassword"
                            dependencies={['newPassword']}
                            rules={[
                              { required: false, message: 'Please confirm your password!' },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error('Your passwords do not match!'));
                                },
                              }),
                            ]}
                          >
                            <Input.Password
                              prefix={<LockOutlined />}
                              className="Allinput"
                              placeholder="Confirm new password"
                            />
                          </Form.Item>
                        </Col>

                        <Col
                          xs={24}
                          sm={24}
                          md={24}
                          xl={24}
                          xxl={20}
                          className="d-flex justify-content-end me-3"
                        >

                          <button className="allsubmitebutton w-44"
                            type="submit"
                            disabled={isLoading}>
                            {isLoading ? "Updating Password..." : "Update Password"}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};